import type { ConsultationRow } from '@/pages/admin/types';
import { levelOf } from '@/pages/admin/utils/level';
import { formatTime, optionText } from '@/pages/admin/utils/fields';

interface ConsultationCardProps {
  row: ConsultationRow;
  onOpen: (row: ConsultationRow) => void;
}

export default function ConsultationCard({ row, onOpen }: ConsultationCardProps) {
  const level = levelOf(row);
  const percent = Math.min(100, Math.round(((row.total_score || 0) / 36) * 100));
  const focus = (row.focus_summary || '')
    .split('、')
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 3);

  const meta = [
    row.age ? `${row.age}` : '',
    row.menstrual ? optionText('menstrual', row.menstrual) : '',
  ].filter(Boolean);

  return (
    <button
      type="button"
      onClick={() => onOpen(row)}
      className="w-full text-left bg-background-50 rounded-xl border border-secondary-200/70 hover:border-primary-300 hover:shadow-sm transition-all p-4 cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${level.badgeClass}`}
          >
            <i className="ri-heart-pulse-line"></i>
            {level.shortLabel}
          </span>
          {meta.length ? (
            <span className="text-sm text-foreground-800 truncate">{meta.join(' · ')}</span>
          ) : (
            <span className="text-sm text-foreground-400">未填基本信息</span>
          )}
        </div>
        <span className="text-[11px] text-foreground-400 shrink-0 pt-1">
          {formatTime(row.created_at)}
        </span>
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-heading font-semibold text-foreground-950 leading-none">
              {row.total_score ?? 0}
            </span>
            <span className="text-[11px] text-foreground-400">/ 36 分 · 已评 {row.rated_count ?? 0}/12 项</span>
          </div>
          <div className="mt-2 h-1.5 w-full bg-secondary-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${level.dotClass}`} style={{ width: `${percent}%` }} />
          </div>
        </div>
        <span className="text-[11px] text-foreground-500 shrink-0 flex items-center gap-0.5">
          <i className="ri-contacts-line"></i>
          {row.contact ? '有联系方式' : '未留联系方式'}
        </span>
      </div>

      {focus.length ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {focus.map((item) => (
            <span
              key={item}
              className="px-2 py-1 rounded-md bg-background-100 text-[11px] text-foreground-600"
            >
              {item}
            </span>
          ))}
        </div>
      ) : null}

      <div className="mt-3 pt-2.5 border-t border-secondary-200/60 flex items-center justify-between text-[11px] text-foreground-400">
        <span>点击查看完整问答</span>
        <i className="ri-arrow-right-s-line text-base"></i>
      </div>
    </button>
  );
}